/* eslint-disable max-lines */
import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import loadable from '@loadable/component';
import { useDispatch, useSelector } from 'react-redux';
import { enableBodyScroll } from 'body-scroll-lock';
import { isDesktop, forDesktop } from '@westwing/ui-kit/breakPoints';
import { CrossIcon } from '@westwing/ui-kit/Icons/StyledIcons';
import useTranslator from '../../../hooks/useTranslator';
import { useScrollLockRef } from '../../Overlays/ScrollableArea';
import useListingPageUrlState from '../../ListingPage/hooks/useListingPageUrlState';
import SearchInputBox from './SearchInputBox';
import {
    SearchWrapper,
    SearchHeader,
    SearchText,
    SearchContainerWrapper,
    SearchContainer,
    CloseButton,
} from './SearchContentElements';
import { SearchProps } from './index';
import { useOverlayOutsideClick } from 'Client/hooks/dom/useOverlayOutsideClick';
import {
    clearDynamicSearchSuggestionsAction,
    fetchDynamicSearchSuggestionsAction,
    setDynamicSearchQueryAction,
    handleEnterKeyDynamicSearchAction,
    isRealDynamicSearchAction,
    fetchDynamicSearchRedirectsIfNeeded,
    clickedDynamicSearchSuggestion,
} from 'Client/redux/dynamicSearch/actions';
import { ARROW_DOWN_KEY_CODE, ARROW_UP_KEY_CODE, ENTER_KEY_CODE } from 'Client/constants/keyboards';
import { MINIMUM_DYNAMIC_SEARCH_QUERY_LENGTH, RedirectSuggestions } from 'Client/redux/dynamicSearch/types';
import { VictorQaClasses } from 'Common/qaIds';
import useGa from 'Client/hooks/useGa';
import { OverlayId } from 'Client/components/Overlays/types';
import { useOverlayUrlService } from 'Client/components/Overlays/useOverlay';
import StateInterface from 'Client/redux/types';
import { getBestSearchRedirect, getSuggestionRedirect } from 'Client/components/OneHeader/DynamicSearch/searchService';
import useBellaPush from 'Client/hooks/useBellaHistoryPush';
import { useLaunchDarklyTracker } from 'Client/LaunchDarklyTracker';
import { HeaderModeType, useLayoutByPage } from 'Client/hooks/useLayoutByPage';

const SearchSuggestions = loadable(() => import('./Suggestions'));
const BurgerMenuContent = loadable(() => import('../Overlays/BurgerFlyout/BurgerMenuContent'));

const StyledBurgerMenuContent = styled(BurgerMenuContent)`
    display: block;
    padding-bottom: 150px;
    ${forDesktop`
        display: none;
    `}
`;

const EMPTY_SEARCH = '';
const NO_SELECTION_INDEX: number = -1;

const SearchContent: React.FC<SearchProps> = ({ suggestions, landingRedirects }) => {
    const dispatch = useDispatch();
    const t = useTranslator();
    const inputRef = useRef<HTMLInputElement>(null);
    const { pullOffOverlay, pushOverlay, isActiveOverlay } = useOverlayUrlService();
    const { headerMode } = useLayoutByPage();
    const [urlState] = useListingPageUrlState();
    const searchQueryString = urlState.search || EMPTY_SEARCH;
    const searchStateValue = useSelector<StateInterface, string>(state => state.dynamicSearchSuggestions.query);
    const [searchInputValue, setSearchInputValue] = useState<string>(searchQueryString);
    const [searchInputValueLastTyped, setSearchInputValueLastTyped] = useState<string>(EMPTY_SEARCH);
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState<number>(NO_SELECTION_INDEX);
    const isEnterKeySearch = useSelector<StateInterface, boolean>(
        state => state.dynamicSearchSuggestions.isEnterKeySearch
    );
    const bellaPush = useBellaPush();
    const gaTracking = useGa();

    const redirectsSuggestions = useSelector<StateInterface, RedirectSuggestions | undefined>(
        state => state.dynamicSearchSuggestions.searchRedirect
    );
    const launchDarklyTracker = useLaunchDarklyTracker();

    const searchFlyoutActiveFromState = isActiveOverlay(OverlayId.SEARCH_FLYOUT);

    const [isSearchOverlayActive, setIsSearchOverlayActive] = useState<boolean>(false);
    useEffect(() => {
        setIsSearchOverlayActive(searchFlyoutActiveFromState);
    }, [searchFlyoutActiveFromState]);

    const scrollLockRef = useScrollLockRef({ reserveScrollBarGap: true }, isSearchOverlayActive);
    const searchPlaceholder = t('Search text');

    const lastSuggestionIndex = suggestions.length - 1;
    const hasSuggestion = suggestions.length > 0;

    const clearSuggestions = () => {
        setSelectedSuggestionIndex(NO_SELECTION_INDEX);
        if (isSearchOverlayActive) {
            dispatch(clearDynamicSearchSuggestionsAction());
        }
    };

    const enablePageScroll = () => {
        if (scrollLockRef.current) {
            enableBodyScroll(scrollLockRef.current);
        }
    };

    const toggleSearchFlyout = (show: boolean) => {
        if (show) {
            pushOverlay(OverlayId.SEARCH_FLYOUT);
        } else {
            pullOffOverlay();
        }
    };

    const resetHeader = () => {
        enablePageScroll();
        toggleSearchFlyout(false);
    };

    const blurInput = () => {
        if (inputRef && inputRef.current) {
            inputRef.current.blur();
        }
    };

    const focusInput = () => {
        if (inputRef && inputRef.current) {
            inputRef.current.focus();
        }
    };

    const setSelectedSuggestion = (
        suggestionName: string = EMPTY_SEARCH,
        suggestionIndex: number = NO_SELECTION_INDEX
    ) => {
        dispatch(setDynamicSearchQueryAction(suggestionName));
        setSearchInputValue(suggestionName.toLowerCase());
        setSelectedSuggestionIndex(suggestionIndex);
    };

    const handleSearchOverlayCloseClick = () => {
        gaTracking.dynamicSearch.trackOverlayCloseClick();
        blurInput();
        resetHeader();
    };

    const handleInputClick = () => {
        gaTracking.dynamicSearch.trackInputClick();
        if (!isDesktop()) {
            gaTracking.dynamicSearch.trackMobileIconClick();
        }
        toggleSearchFlyout(true);
    };

    const handleClearClick = () => {
        clearSuggestions();
        focusInput();
    };

    const handleSuggestionClick = (name: string, index: number) => {
        gaTracking.dynamicSearch.trackSuggestionPosition(name, index);
        gaTracking.dynamicSearch.trackSearchUrlParam(name);

        enablePageScroll();
        clearSuggestions();
        dispatch(isRealDynamicSearchAction(true));
        dispatch(setDynamicSearchQueryAction(name));
        dispatch(fetchDynamicSearchRedirectsIfNeeded(name));
        dispatch(handleEnterKeyDynamicSearchAction(true));
        dispatch(clickedDynamicSearchSuggestion(name));
        launchDarklyTracker.trackMetric('bella-search-submission-rate');
        launchDarklyTracker.trackMetric('bella-suggestions-clicks-rate');
    };

    const handleInputChange = (value: string): void => {
        setSearchInputValue(value);
        if (value.length > MINIMUM_DYNAMIC_SEARCH_QUERY_LENGTH) {
            setSearchInputValueLastTyped(value);
            setSelectedSuggestionIndex(NO_SELECTION_INDEX);
            dispatch(setDynamicSearchQueryAction(value));
            dispatch(fetchDynamicSearchSuggestionsAction());
        } else {
            clearSuggestions();
        }
    };

    const handleInputFocus = (): void => {
        if (searchInputValue.length > MINIMUM_DYNAMIC_SEARCH_QUERY_LENGTH) {
            dispatch(setDynamicSearchQueryAction(searchInputValue));
            dispatch(clearDynamicSearchSuggestionsAction(true));
        }
    };

    const handleArrowUpKeyUp = () => {
        if (!hasSuggestion) {
            return;
        }
        if (selectedSuggestionIndex === 0) {
            setSelectedSuggestion(searchInputValueLastTyped, NO_SELECTION_INDEX);
            return;
        }
        if (selectedSuggestionIndex < 0) {
            setSelectedSuggestion(suggestions[lastSuggestionIndex].name, lastSuggestionIndex);
            return;
        }
        const previousSuggestionIndex = selectedSuggestionIndex - 1;
        setSelectedSuggestion(suggestions[previousSuggestionIndex].name, previousSuggestionIndex);
    };

    const handleArrowDownKeyUp = () => {
        if (!hasSuggestion) {
            return;
        }
        if (selectedSuggestionIndex === lastSuggestionIndex) {
            setSelectedSuggestion(searchInputValueLastTyped, NO_SELECTION_INDEX);
            return;
        }
        if (selectedSuggestionIndex > lastSuggestionIndex) {
            setSelectedSuggestion(suggestions[lastSuggestionIndex].name, lastSuggestionIndex);
            return;
        }
        const nextSuggestionIndex = selectedSuggestionIndex + 1;
        setSelectedSuggestion(suggestions[nextSuggestionIndex].name, nextSuggestionIndex);
    };

    const handleEnterKeyUp = () => {
        blurInput();
        // To ensure metric is tracked from input box enter key press only
        if (selectedSuggestionIndex === NO_SELECTION_INDEX) {
            launchDarklyTracker.trackMetric('bella-search-submission-rate');
        }

        dispatch(fetchDynamicSearchRedirectsIfNeeded(searchStateValue));
        dispatch(isRealDynamicSearchAction(true));
        dispatch(handleEnterKeyDynamicSearchAction(true));
    };

    const handleNavigateOnEnterKeyPress = () => {
        let url;
        let isRedirect;
        const selectedSuggestionName = suggestions[selectedSuggestionIndex]?.name || '';

        if (redirectsSuggestions && Object.values(redirectsSuggestions).filter(x => x.length).length) {
            ({ url, isRedirect } = getSuggestionRedirect(redirectsSuggestions, searchStateValue, ''));
        } else {
            url = `/all-products?q=${encodeURIComponent(selectedSuggestionName || searchStateValue)}`;
            const refreshBestSearchRedirect =
                searchStateValue.toLocaleLowerCase() !== selectedSuggestionName.toLocaleLowerCase();

            if (refreshBestSearchRedirect) {
                ({ url, isRedirect } = getBestSearchRedirect(redirectsSuggestions, landingRedirects, searchStateValue));
            } else {
                dispatch(clearDynamicSearchSuggestionsAction(true));
            }
        }

        if (selectedSuggestionName) {
            dispatch(clickedDynamicSearchSuggestion(selectedSuggestionName));
            gaTracking.dynamicSearch.trackSuggestionPosition(selectedSuggestionName, selectedSuggestionIndex);
            launchDarklyTracker.trackMetric('bella-suggestions-clicks-rate');
        }

        gaTracking.dynamicSearch.trackSearchPerformed(searchStateValue);
        if (isRedirect) {
            gaTracking.dynamicSearch.trackSearchUrlParam(searchStateValue);
        }

        bellaPush(url, true);
        enablePageScroll();
        setSelectedSuggestion(EMPTY_SEARCH, NO_SELECTION_INDEX);

        dispatch(isRealDynamicSearchAction(true));
        dispatch(handleEnterKeyDynamicSearchAction(false));
    };

    useEffect(() => {
        if (searchStateValue && isEnterKeySearch) {
            handleNavigateOnEnterKeyPress();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [redirectsSuggestions]);

    const handleInputKeyUp = (keyCode: number) => {
        switch (keyCode) {
            case ARROW_UP_KEY_CODE: {
                handleArrowUpKeyUp();
                break;
            }
            case ARROW_DOWN_KEY_CODE: {
                handleArrowDownKeyUp();
                break;
            }
            case ENTER_KEY_CODE: {
                handleEnterKeyUp();
                break;
            }
            default:
                break;
        }
    };

    // This functions helps to prevent the weird default cursor behaviour of jumping between the beginning and the end
    // of the input field text when scrolling through the suggestion items with the keyboard arrow keys.
    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.keyCode === ARROW_UP_KEY_CODE && hasSuggestion) {
            e.preventDefault();
        }
    };

    const wrapperContentRef = useOverlayOutsideClick(clearSuggestions, true);

    useEffect(() => {
        setSearchInputValue(searchQueryString);
    }, [searchQueryString]);

    const shouldShowCategoryMenu =
        (!searchInputValue || searchInputValue === searchQueryString) && isSearchOverlayActive && !hasSuggestion;

    return (
        <SearchWrapper
            data-testid="search-wrapper"
            isSearchOverlayActive={isSearchOverlayActive}
            hideOnMobile={headerMode === HeaderModeType.MINI}
            ref={wrapperContentRef}
        >
            <SearchHeader role="heading" isSearchOverlayActive={isSearchOverlayActive}>
                <SearchText data-testid="search-overlay-title">{t('SearchOverlay_search')}</SearchText>
                <CloseButton onClick={handleSearchOverlayCloseClick} data-testid="search-close-btn">
                    <CrossIcon width={13} height={13} strokeWidth={5} />
                </CloseButton>
            </SearchHeader>
            <SearchInputBox
                inputRef={inputRef}
                isSearchOverlayActive={isSearchOverlayActive}
                placeholder={searchPlaceholder}
                value={searchInputValue}
                onChange={handleInputChange}
                onKeyUp={handleInputKeyUp}
                onKeyDown={handleInputKeyDown}
                onFocus={handleInputFocus}
                onClick={handleInputClick}
                onClearClick={handleClearClick}
            />
            <SearchContainerWrapper>
                <SearchContainer
                    role="contentinfo"
                    ref={!isDesktop() && isSearchOverlayActive ? scrollLockRef : null}
                    isSearchOverlayActive={isSearchOverlayActive}
                    className={VictorQaClasses.headerSearchSuggestionFlyout}
                >
                    {hasSuggestion && (
                        <SearchSuggestions
                            isSearchOverlayActive={isSearchOverlayActive}
                            suggestions={suggestions}
                            selectedSuggestionIndex={selectedSuggestionIndex}
                            onSuggestionClick={handleSuggestionClick}
                        />
                    )}
                    {shouldShowCategoryMenu && (
                        <StyledBurgerMenuContent
                            strict
                            title="SearchOverlay_categories"
                            bordered
                            hideCloseIcon
                            search
                            searchCloseFunction={resetHeader}
                        />
                    )}
                </SearchContainer>
            </SearchContainerWrapper>
        </SearchWrapper>
    );
};

export default SearchContent;
